import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { Truck, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const FLEET_IMGS = [
  "https://images.unsplash.com/photo-1631744591853-998c4308bbb0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwzfHx0cnVjayUyMGZsZWV0JTIwaGlnaHdheXxlbnwwfHx8fDE3ODU5Mzk4OTZ8MA&ixlib=rb-4.1.0&q=85",
  "https://static.prod-images.emergentagent.com/jobs/ea9d6785-086f-4359-a361-246313ad7479/images/8bed0d3b2ce7615531e31c0c8d99ba59506a1aca64c65ceb921e2627db0a0c3d.jpeg",
  "https://static.prod-images.emergentagent.com/jobs/ea9d6785-086f-4359-a361-246313ad7479/images/aa94af0190608d54ba0512dcf394f5f1f7936df0191fc768af8298ad3913337f.jpeg",
  "https://static.prod-images.emergentagent.com/jobs/ea9d6785-086f-4359-a361-246313ad7479/images/ecc29a85fb19781b3b09f5ab8973bc9c67b42421ace3ed5e5bc981677e5cecef.jpeg",
];

function DemoOtpBanner({ code }) {
  if (!code) return null;
  return (
    <div
      data-testid="demo-otp-banner"
      className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800"
    >
      Demo mode — your OTP is <span className="font-bold tracking-widest">{code}</span>
    </div>
  );
}

function ResendOtp({ onResend, disabled, testid }) {
  const [left, setLeft] = useState(30);
  useEffect(() => {
    if (left <= 0) return;
    const t = setTimeout(() => setLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [left]);
  return (
    <button
      type="button"
      onClick={async () => { setLeft(30); await onResend(); }}
      disabled={disabled || left > 0}
      className="w-full text-center text-xs font-medium text-primary hover:underline disabled:cursor-default disabled:text-muted-foreground"
      data-testid={testid}
    >
      {left > 0 ? `Resend OTP in ${left}s` : "Resend OTP"}
    </button>
  );
}

export default function Login() {
  const {
    user,
    ready,
    login,
    verifyLoginOtp,
    forgotRequestOtp,
    resetPassword,
  } = useAuth();

  const [mode, setMode] = useState("signin"); // signin | forgot
  const [error, setError] = useState("");
  const [vision, setVision] = useState("");
  const [slide, setSlide] = useState(0);
  useEffect(() => {
    api.get("/public/company-vision").then((r) => setVision(r.data?.vision || "")).catch(() => {});
  }, []);
  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % FLEET_IMGS.length), 5000);
    return () => clearInterval(t);
  }, []);
  const [loading, setLoading] = useState(false);

  // password login
  const [loginId, setLoginId] = useState("");
  const [branch, setBranch] = useState("");
  const [pwStep, setPwStep] = useState("cred"); // cred | otp
  const [loginOtp, setLoginOtp] = useState("");
  const [loginDemo, setLoginDemo] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [password, setPassword] = useState("");
  const [staffCode, setStaffCode] = useState("");

  // forgot password
  const [fId, setFId] = useState("");
  const [fBranch, setFBranch] = useState("");
  const [fStep, setFStep] = useState("identify");
  const [fCode, setFCode] = useState("");
  const [fNewPass, setFNewPass] = useState("");
  const [fDemo, setFDemo] = useState("");
  const [fMasked, setFMasked] = useState("");

  if (ready && user) return <Navigate to="/" replace />;

  const wrap = async (fn) => {
    setError("");
    setLoading(true);
    try {
      await fn();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitPassword = (e) => {
    e.preventDefault();
    wrap(async () => {
      const data = await login({ user_id: loginId, password, office: branch.trim(), staff_code: staffCode.trim() });
      if (data?.otp_required) {
        setMaskedEmail(data.mobile || data.email || "");
        setLoginDemo(data.demo_otp || "");
        setLoginOtp("");
        setPwStep("otp");
        toast.success(data.message || "OTP sent to your mobile");
      }
    });
  };

  const submitLoginOtp = (e) => {
    e.preventDefault();
    wrap(() => verifyLoginOtp({ user_id: loginId }, loginOtp));
  };

  const resendLoginOtp = () =>
    wrap(async () => {
      const data = await login({ user_id: loginId, password, office: branch.trim(), staff_code: staffCode.trim() });
      setLoginDemo(data?.demo_otp || "");
      toast.success("New OTP sent to your mobile");
    });

  const sendForgotOtp = (e) => {
    e.preventDefault();
    wrap(async () => {
      const data = await forgotRequestOtp({ user_id: fId, office: fBranch.trim() });
      setFDemo(data.demo_otp || "");
      setFMasked(data.email || "");
      setFStep("reset");
      toast.success(data.message || "OTP sent to your email");
    });
  };

  const resendForgotOtp = () =>
    wrap(async () => {
      const data = await forgotRequestOtp({ user_id: fId, office: fBranch.trim() });
      setFDemo(data?.demo_otp || "");
      setFMasked(data?.email || fMasked);
      toast.success("New OTP sent to your email");
    });

  const submitReset = (e) => {
    e.preventDefault();
    wrap(async () => {
      await resetPassword({ user_id: fId }, fCode, fNewPass);
      toast.success("Password reset. Please sign in.");
      setMode("signin");
      setFStep("identify");
      setPassword("");
    });
  };

  const heading = mode === "forgot" ? "Reset your password" : "Welcome back";
  const sub =
    mode === "forgot"
      ? "Enter your User ID or email — we'll send an OTP to your registered email."
      : "Sign in to manage your operations.";

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="relative hidden overflow-hidden bg-[#0F172A] lg:block">
        {FLEET_IMGS.map((src, i) => (
          <img
            key={src}
            src={src}
            alt="NTC fleet"
            className="absolute inset-0 h-full w-full object-cover transition-opacity duration-1000"
            style={{ opacity: slide === i ? 0.3 : 0 }}
            data-testid={`login-slide-${i}`}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-[#0F172A]/70 to-transparent" />
        <div className="relative flex h-full flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <img src="/ntc-logo.jpg" alt="NTC logo" className="h-12 w-12 rounded-md object-contain bg-white/90 p-0.5" />
            <div>
              <div className="font-display text-lg font-bold text-white">Nisha Transport Corporation</div>
            </div>
          </div>
          <div>
            <h2 className="font-display text-4xl font-bold leading-tight text-white">
              Empowering your
              <br /> business success.
            </h2>
            {vision ? (
              <p className="mt-4 max-w-md border-l-2 border-emerald-400 pl-3 text-sm italic leading-relaxed text-slate-200" data-testid="login-vision">
                "{vision}"
              </p>
            ) : (
              <p className="mt-4 max-w-md text-sm leading-relaxed text-slate-300">
                One command centre for shipments, invoicing and expenses — with live accounting insights across
                your entire fleet.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center bg-background px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <img src="/ntc-logo.jpg" alt="NTC logo" className="h-10 w-10 rounded-md object-contain" />
            <span className="font-display text-lg font-bold">Nisha Transport Corporation</span>
          </div>

          <div className="mb-1 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
            <Truck className="h-3.5 w-3.5" /> Logistics Accounting Suite
          </div>
          <h1 className="mt-4 font-display text-3xl font-bold tracking-tight">{heading}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{sub}</p>

          {error && (
            <div data-testid="auth-error" className="mt-6 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}

          {/* SIGN IN */}
          {mode === "signin" && (
            <div className="mt-6">
                {pwStep === "cred" ? (
                  <form onSubmit={submitPassword} className="space-y-4">
                    <div className="grid gap-1.5">
                      <Label htmlFor="login-id">User ID</Label>
                      <Input id="login-id" data-testid="login-userid" value={loginId} onChange={(e) => setLoginId(e.target.value)} required placeholder="Enter your User ID" />
                    </div>
                    <div className="grid gap-1.5">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="password">Password</Label>
                        <button type="button" onClick={() => { setMode("forgot"); setError(""); }} className="text-xs font-medium text-primary hover:underline" data-testid="forgot-password-link">
                          Forgot password?
                        </button>
                      </div>
                      <Input id="password" type="password" data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" />
                    </div>
                    <div className="grid gap-1.5">
                      <Label htmlFor="login-staff-code">Staff Code</Label>
                      <Input id="login-staff-code" data-testid="login-staff-code" value={staffCode} onChange={(e) => setStaffCode(e.target.value)} required placeholder="Enter your Staff Code" autoComplete="off" />
                    </div>
                    <div className="grid gap-1.5">
                      <Label htmlFor="login-branch">Branch</Label>
                      <Input id="login-branch" data-testid="login-branch" value={branch} onChange={(e) => setBranch(e.target.value)} required placeholder="Enter your branch" autoComplete="off" />
                    </div>
                    <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">
                      {loading ? "Please wait…" : "Sign in"}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </form>
                ) : (
                  <form onSubmit={submitLoginOtp} className="space-y-4">
                    <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800" data-testid="login-otp-sent-banner">
                      OTP sent to mobile <span className="font-semibold">{maskedEmail}</span>. Enter it below to complete sign in.
                    </div>
                    <DemoOtpBanner code={loginDemo} />
                    <div className="grid gap-1.5">
                      <Label htmlFor="login-otp">Enter OTP</Label>
                      <Input id="login-otp" data-testid="login-otp-code" value={loginOtp} onChange={(e) => setLoginOtp(e.target.value)} required placeholder="6-digit code" maxLength={6} autoFocus />
                    </div>
                    <Button type="submit" className="w-full" disabled={loading} data-testid="login-otp-verify">
                      {loading ? "Verifying…" : "Verify & Sign in"}
                    </Button>
                    <ResendOtp onResend={resendLoginOtp} disabled={loading} testid="login-otp-resend" />
                    <button type="button" onClick={() => { setPwStep("cred"); setLoginOtp(""); setLoginDemo(""); setError(""); }} className="w-full text-center text-xs text-muted-foreground hover:underline" data-testid="login-otp-back">
                      ← Back
                    </button>
                  </form>
                )}
            </div>
          )}

          {/* FORGOT PASSWORD */}
          {mode === "forgot" && (
            <div className="mt-6">
              {fStep === "identify" ? (
                <form onSubmit={sendForgotOtp} className="space-y-4">
                  <div className="grid gap-1.5">
                    <Label htmlFor="f-id">User ID or Email</Label>
                    <Input id="f-id" data-testid="forgot-identifier" value={fId} onChange={(e) => setFId(e.target.value)} required placeholder="Enter your User ID or email" />
                  </div>
                  <div className="grid gap-1.5">
                    <Label htmlFor="f-branch">Branch</Label>
                    <Input id="f-branch" data-testid="forgot-branch" value={fBranch} onChange={(e) => setFBranch(e.target.value)} required placeholder="Enter your branch" autoComplete="off" />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="forgot-send-otp-btn">
                    {loading ? "Sending…" : "Send OTP to Email"}
                  </Button>
                </form>
              ) : (
                <form onSubmit={submitReset} className="space-y-4">
                  {fMasked && (
                    <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800" data-testid="forgot-otp-sent-banner">
                      OTP sent to <span className="font-semibold">{fMasked}</span>.
                    </div>
                  )}
                  <DemoOtpBanner code={fDemo} />
                  <div className="grid gap-1.5">
                    <Label htmlFor="f-code">OTP</Label>
                    <Input id="f-code" data-testid="forgot-code" value={fCode} onChange={(e) => setFCode(e.target.value)} required placeholder="6-digit code" maxLength={6} />
                  </div>
                  <div className="grid gap-1.5">
                    <Label htmlFor="f-newpass">New password</Label>
                    <Input id="f-newpass" type="password" data-testid="forgot-new-password" value={fNewPass} onChange={(e) => setFNewPass(e.target.value)} required placeholder="Min 6 characters" />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="reset-password-btn">
                    {loading ? "Resetting…" : "Reset password"}
                  </Button>
                  <ResendOtp onResend={resendForgotOtp} disabled={loading} testid="forgot-otp-resend" />
                </form>
              )}
              <button type="button" onClick={() => { setMode("signin"); setFStep("identify"); setError(""); }} className="mt-4 w-full text-center text-sm text-muted-foreground hover:underline" data-testid="back-to-signin">
                ← Back to sign in
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
